// Live network detection. Renders a Stitch-themed offline panel when the
// device loses signal mid-session. On native (Capacitor) we use @capacitor/network;
// in the browser we fall back to navigator.onLine + online/offline events.
import { useEffect, useState, type ReactNode } from "react";
import { Network } from "@capacitor/network";
import { WifiOff, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { isNativeApp } from "@/lib/platform";

interface Props {
  children: ReactNode;
}

export function OfflineGate({ children }: Props) {
  const [online, setOnline] = useState(true);

  useEffect(() => {
    let mounted = true;
    let removeNative: (() => void) | undefined;

    (async () => {
      if (isNativeApp()) {
        try {
          const status = await Network.getStatus();
          if (mounted) setOnline(status.connected);
          const handle = await Network.addListener("networkStatusChange", (s) => {
            if (mounted) setOnline(s.connected);
          });
          removeNative = () => handle.remove();
        } catch {
          if (mounted) setOnline(true);
        }
      } else {
        setOnline(navigator.onLine);
        const goOnline = () => mounted && setOnline(true);
        const goOffline = () => mounted && setOnline(false);
        window.addEventListener("online", goOnline);
        window.addEventListener("offline", goOffline);
        removeNative = () => {
          window.removeEventListener("online", goOnline);
          window.removeEventListener("offline", goOffline);
        };
      }
    })();

    return () => {
      mounted = false;
      removeNative?.();
    };
  }, []);

  if (online) return <>{children}</>;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background px-6">
      <div className="w-full max-w-sm text-center">
        <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/60 shadow-lg shadow-primary/30">
          <WifiOff className="h-10 w-10 text-primary-foreground" />
        </div>
        <h1 className="mb-2 text-xl font-bold tracking-tight">No Internet Connection</h1>
        <p className="mb-7 text-sm text-muted-foreground">
          FitAura AI needs internet to generate try-ons. Please check your signal and try again.
        </p>
        <Button
          size="lg"
          className="w-full"
          onClick={async () => {
            // Re-check connection only. NEVER call window.location.reload()
            // on native — with `server.url` set, a reload while offline can
            // bounce the user into the external browser. We just re-query
            // the Network plugin and let the listener update on its own
            // when the device reconnects.
            if (isNativeApp()) {
              try {
                const s = await Network.getStatus();
                setOnline(s.connected);
              } catch {
                /* ignore — listener will fire when network returns */
              }
            } else {
              setOnline(navigator.onLine);
            }
          }}
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Retry
        </Button>
      </div>
    </div>
  );
}
